import React, { useState } from "react"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"

interface FleetQuoteFormProps {
  onSuccess?: () => void
}

export default function FleetQuoteForm({ onSuccess }: FleetQuoteFormProps) {
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    vehicleType: "",
    numVehicles: "",
    yearMakeModel: "",
    wrapCoverage: [] as string[],
    timeline: "",
    hasArtwork: "",
    artworkFiles: [] as string[],
    vehiclePhotos: [] as string[],
    needsGraphicDesign: false,
    notes: ""
  })

  const [uploadingArtwork, setUploadingArtwork] = useState(false)
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      wrapCoverage: prev.wrapCoverage.includes(value)
        ? prev.wrapCoverage.filter(v => v !== value)
        : [...prev.wrapCoverage, value]
    }))
  }

  const handleFileUpload = async (files: FileList | null, type: "artwork" | "photos") => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    
    if (type === "artwork") {
      setUploadingArtwork(true)
    } else {
      setUploadingPhotos(true)
    }

    try {
      const uploadResult = await lumi.tools.file.upload(fileArray)
      const successfulUrls = uploadResult
        .filter(r => r.fileUrl && !r.uploadError)
        .map(r => r.fileUrl!)

      if (type === "artwork") {
        setFormData(prev => ({ ...prev, artworkFiles: [...prev.artworkFiles, ...successfulUrls] }))
        toast.success(`${successfulUrls.length} artwork file(s) uploaded`)
      } else {
        setFormData(prev => ({ ...prev, vehiclePhotos: [...prev.vehiclePhotos, ...successfulUrls] }))
        toast.success(`${successfulUrls.length} photo(s) uploaded`)
      }
    } catch (error: any) {
      toast.error(`Upload failed: ${error?.message || "Unknown error"}`)
    } finally {
      if (type === "artwork") {
        setUploadingArtwork(false)
      } else {
        setUploadingPhotos(false)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields")
      return
    }

    if (formData.wrapCoverage.length === 0) {
      toast.error("Please select at least one wrap coverage option")
      return
    }

    setSubmitting(true)

    try {
      const response = await lumi.functions.invoke("fleet-quote-submit", {
        method: "POST",
        body: formData
      })

      if (response?.success) {
        toast.success("Thank you — we've received your request and will be in touch shortly.")
        setFormData({
          companyName: "",
          contactName: "",
          email: "",
          phone: "",
          vehicleType: "",
          numVehicles: "",
          yearMakeModel: "",
          wrapCoverage: [],
          timeline: "",
          hasArtwork: "",
          artworkFiles: [],
          vehiclePhotos: [],
          needsGraphicDesign: false,
          notes: ""
        })
        onSuccess?.()
      } else {
        toast.error("Failed to submit request. Please try again.")
      }
    } catch (error: any) {
      console.error("Fleet quote submission error:", error)
      toast.error(`Error: ${error?.message || "Failed to submit request"}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Business Information */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 border-b border-cyan-500/20 pb-2">Business Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
              placeholder="Your Company Name"
            />
          </div>
          
          <div>
            <label htmlFor="contactName" className="block text-sm font-medium text-gray-300 mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              id="contactName"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
              placeholder="John Smith"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
              placeholder="contact@company.com"
            />
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
              placeholder="858-282-8119"
            />
          </div>
        </div>
      </div>

      {/* Vehicle Information */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 border-b border-cyan-500/20 pb-2">Vehicle Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="vehicleType" className="block text-sm font-medium text-gray-300 mb-2">
              Vehicle Type *
            </label>
            <select
              id="vehicleType"
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
            >
              <option value="">Select type</option>
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="Truck">Truck</option>
              <option value="Van">Van</option>
              <option value="Box Truck">Box Truck</option>
              <option value="Trailer">Trailer</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="numVehicles" className="block text-sm font-medium text-gray-300 mb-2">
              Number of Vehicles *
            </label>
            <input
              type="text"
              id="numVehicles"
              name="numVehicles"
              value={formData.numVehicles}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
              placeholder="e.g., 5"
            />
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="yearMakeModel" className="block text-sm font-medium text-gray-300 mb-2">
            Year / Make / Model (or "Mixed Fleet")
          </label>
          <input
            type="text"
            id="yearMakeModel"
            name="yearMakeModel"
            value={formData.yearMakeModel}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
            placeholder="2023 Ford Transit, 2022 Chevy Silverado, etc. or 'Mixed Fleet'"
          />
        </div>
      </div>

      {/* Wrap Details */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 border-b border-cyan-500/20 pb-2">Wrap Details</h3>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Wrap Coverage *
          </label>
          <div className="space-y-3">
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.wrapCoverage.includes("Full Wrap")}
                onChange={() => handleCheckboxChange("Full Wrap")}
                className="w-5 h-5 rounded border-gray-700 text-cyan-500 focus:ring-cyan-500" 
              />
              <span>Full Wrap</span>
            </label>
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.wrapCoverage.includes("Partial Wrap")}
                onChange={() => handleCheckboxChange("Partial Wrap")}
                className="w-5 h-5 rounded border-gray-700 text-cyan-500 focus:ring-cyan-500" 
              />
              <span>Partial Wrap</span>
            </label>
            <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.wrapCoverage.includes("Logos / Decals Only")}
                onChange={() => handleCheckboxChange("Logos / Decals Only")}
                className="w-5 h-5 rounded border-gray-700 text-cyan-500 focus:ring-cyan-500" 
              />
              <span>Logos / Decals Only</span>
            </label>
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="timeline" className="block text-sm font-medium text-gray-300 mb-2">
            Timeline *
          </label>
          <select
            id="timeline"
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
          >
            <option value="">Select timeline</option>
            <option value="ASAP">ASAP</option>
            <option value="1–2 Weeks">1–2 Weeks</option>
            <option value="1 Month">1 Month</option>
            <option value="Flexible">Flexible</option>
          </select>
        </div>
      </div>

      {/* Design Assets */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 border-b border-cyan-500/20 pb-2">Design Assets</h3>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Do you already have artwork? *
          </label>
          <div className="flex gap-6 mb-4">
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input 
                type="radio" 
                name="hasArtwork" 
                value="yes" 
                checked={formData.hasArtwork === "yes"}
                onChange={handleChange}
                className="w-4 h-4 text-cyan-500 focus:ring-cyan-500" 
              />
              <span>Yes</span>
            </label>
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input 
                type="radio" 
                name="hasArtwork" 
                value="no" 
                checked={formData.hasArtwork === "no"}
                onChange={handleChange}
                className="w-4 h-4 text-cyan-500 focus:ring-cyan-500" 
              />
              <span>No</span>
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="artworkUpload" className="block text-sm font-medium text-gray-300 mb-2">
            Upload Artwork Files (Optional)
          </label>
          <input
            type="file"
            id="artworkUpload"
            multiple
            accept=".ai,.eps,.pdf,.svg,.png,.jpg,.jpeg"
            onChange={(e) => handleFileUpload(e.target.files, "artwork")}
            disabled={uploadingArtwork}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-500 file:text-white hover:file:bg-cyan-600 disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-2">Vector files preferred (AI, EPS, PDF)</p>
          {formData.artworkFiles.length > 0 && (
            <p className="text-sm text-cyan-400 mt-2">{formData.artworkFiles.length} file(s) uploaded</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="photoUpload" className="block text-sm font-medium text-gray-300 mb-2">
            Upload Vehicle Photos (Optional)
          </label>
          <input
            type="file"
            id="photoUpload"
            multiple
            accept=".jpg,.jpeg,.png,.webp"
            onChange={(e) => handleFileUpload(e.target.files, "photos")}
            disabled={uploadingPhotos}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-500 file:text-white hover:file:bg-cyan-600 disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-2">JPG, PNG, WEBP formats</p>
          {formData.vehiclePhotos.length > 0 && (
            <p className="text-sm text-cyan-400 mt-2">{formData.vehiclePhotos.length} photo(s) uploaded</p>
          )}
        </div>

        <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
          <input 
            type="checkbox" 
            checked={formData.needsGraphicDesign}
            onChange={(e) => setFormData(prev => ({ ...prev, needsGraphicDesign: e.target.checked }))}
            className="w-5 h-5 rounded border-gray-700 text-cyan-500 focus:ring-cyan-500" 
          />
          <span>I need graphic design services</span>
        </label>
      </div>

      {/* Additional Details */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 border-b border-cyan-500/20 pb-2">Additional Details</h3>
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-2">
            Notes / Special Requests
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={5}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
            placeholder="Tell us about your branding goals, any specific requirements, preferred colors, or other details..."
          />
        </div>
      </div>
      
      <button
        type="submit"
        disabled={submitting || uploadingArtwork || uploadingPhotos}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "Submitting..." : "Request Fleet Quote"}
      </button>
      
      <p className="text-xs text-gray-400 text-center">
        We'll contact you within 24 hours with a detailed quote and project timeline
      </p>
    </form>
  )
}
